import { useEffect, useMemo } from 'react';

import { RenderingQuestionnaire } from '@zinnia/form-engine-sdk';

import {
  getSectionGroupWithIndicesId,
  getSectionsForSectionGroup,
  NavigationRenderingSection,
  SectionGroupSelectionProps,
  SectionNavigationData,
} from '../../Helpers/assistedApplication/sectionGroups';
import { SectionGroupId } from '../../Helpers/questionnaireAnswers';
import { useCarrierContext, useSelector } from '../../Hooks';
import { useAssistedApplicationContext } from '../../Hooks/useAssistedApplicationContext';
import { useQuestionnaireEngine } from '../useQuestionnaireEngine';

// Gets all sections for a section group with updated visited indices
export function useAllSections(sectionGroupSelectionProps: SectionGroupSelectionProps): NavigationRenderingSection[] {
  const { features } = useCarrierContext();
  const { isPaymentReadyToSubmit } = useSelector((store) => store.leadPlatform.payment);
  const { sectionGroup, visitedSectionIndicesByGroup } = sectionGroupSelectionProps;
  const { currentProposedInsuredIndex, currentSectionGroupId, proposedInsuredTabs } = useAssistedApplicationContext();

  return useMemo((): NavigationRenderingSection[] => {
    const surrogateId = proposedInsuredTabs[currentProposedInsuredIndex]?.surrogateId ?? '';
    const sectionGroupWithIndices = visitedSectionIndicesByGroup?.find(
      (group) => group.id === getSectionGroupWithIndicesId(currentSectionGroupId, surrogateId),
    );

    return getSectionsForSectionGroup(sectionGroup.sections, sectionGroupWithIndices?.indices || []);
  }, [
    currentProposedInsuredIndex,
    currentSectionGroupId,
    features.payments.enabled,
    isPaymentReadyToSubmit,
    proposedInsuredTabs,
    sectionGroup.sections,
    visitedSectionIndicesByGroup,
  ]);
}

export function useSectionNavigation(renderingQuestionnaire: RenderingQuestionnaire): {
  nextSectionData: SectionNavigationData | undefined;
  previousSectionData: SectionNavigationData | undefined;
} {
  const { activeSectionIndex, currentSectionGroupId, currentProposedInsuredIndex } = useAssistedApplicationContext();

  const allSections = [];
  for (let i = 0; i < renderingQuestionnaire.length; i++) {
    let proposedInsuredIndex: number | undefined;
    if (renderingQuestionnaire[i].id.includes(SectionGroupId.insuredPeople)) {
      const parts = renderingQuestionnaire[i].id.split('.');
      proposedInsuredIndex = parseInt(parts[parts.length - 1], 10);
    }

    let visibleIndex = 0;
    for (let j = 0; j < renderingQuestionnaire[i].sections.length; j++) {
      if (renderingQuestionnaire[i].sections[j].visible === false) continue;
      allSections.push({
        index: visibleIndex,
        groupId: renderingQuestionnaire[i].id.includes(SectionGroupId.insuredPeople)
          ? SectionGroupId.insuredPeople
          : renderingQuestionnaire[i].id,
        proposedInsuredIndex,
        section: renderingQuestionnaire[i].sections[j],
      } as SectionNavigationData);
      visibleIndex++;
    }
  }

  const currentSectionIndex = allSections.findIndex(
    (section) =>
      section.index === activeSectionIndex &&
      section.groupId === currentSectionGroupId &&
      (section.proposedInsuredIndex === currentProposedInsuredIndex || section.proposedInsuredIndex === undefined),
  );

  return {
    nextSectionData: allSections[currentSectionIndex + 1] || undefined,
    previousSectionData: allSections[currentSectionIndex - 1] || undefined,
  };
}

export function useSectionViewPresets(section: NavigationRenderingSection | undefined): void {
  const { questionnaireEngine } = useQuestionnaireEngine();
  useEffect(() => {
    if (!section) return;
    questionnaireEngine.setDisplayError(section.visited && !section.completed);
  }, [section]);
}
